// Dynamic permission and department handling utilities
// Aligned with backend: docs/backend_docs/RBAC_Roles_And_Permissions.md
// Backend format: module.feature.access (e.g. sales.leads.view, projects.details.update)

/**
 * Admin role names that may get broader access (optional; backend sends explicit permissions)
 */
export const DEFAULT_ADMIN_ROLES = ['super-admin', 'superadmin', 'company-admin', 'admin', 'sub-admin'];

/**
 * Known backend action suffixes (last segment in module.feature.action).
 * Wildcard '*' grants 'manage' on the subject.
 */
const BACKEND_ACTION_MAP = {
    view: 'read',
    read: 'read',
    create: 'create',
    update: 'update',
    delete: 'delete',
    assign: 'update',
    switch: 'update',
    approve: 'update',
    request: 'create',
    resolve: 'update',
    close: 'update',
    convert: 'create',
    move: 'update',
    manage: 'manage',
    '*': 'manage',
    update_stage: 'update',
    update_status: 'update',
    respond: 'update',
    submit: 'update',
    submit_to_client: 'update',
};

/**
 * Converts backend permission string (module.feature.access) to CASL format [action, subject].
 *
 * Backend format (3+ segments):
 * - sales.leads.view       -> ['read', 'SalesLeads']
 * - sales.leads.create     -> ['create', 'SalesLeads']
 * - projects.details.view  -> ['read', 'ProjectsDetails']
 * - projects.change_requests.create -> ['create', 'ProjectsChangeRequests']
 * - saas.tenants.*         -> ['manage', 'SaasTenants']
 * - chat.messages.with_clients -> ['manage', 'ChatMessagesWithClients'] (special feature)
 *
 * Legacy 2-part (resource.action) still supported:
 * - dashboard.view -> ['read', 'Dashboard']
 */
export const parsePermission = (permissionString) => {
    if (!permissionString || typeof permissionString !== 'string') {
        return null;
    }

    const parts = permissionString.split('.').filter(Boolean);
    if (parts.length < 2) {
        return null;
    }

    // Legacy: exactly 2 parts -> resource.action
    if (parts.length === 2) {
        const [resourceKey, actionKey] = parts;
        const action = BACKEND_ACTION_MAP[actionKey] || actionKey;
        const subject = resourceKey.charAt(0).toUpperCase() + resourceKey.slice(1).replace(/_/g, '');
        return [action, subject];
    }

    // module.feature.action or module.feature.subfeature.action (or module.feature.*)
    const lastPart = parts[parts.length - 1];
    const isWildcard = lastPart === '*';
    const isKnownAction = Object.prototype.hasOwnProperty.call(BACKEND_ACTION_MAP, lastPart);

    let action;
    let subjectParts;

    if (isWildcard) {
        action = 'manage';
        subjectParts = parts.slice(0, -1);
    } else if (isKnownAction) {
        action = BACKEND_ACTION_MAP[lastPart];
        subjectParts = parts.slice(0, -1);
    } else {
        // e.g. chat.messages.with_clients -> treat whole thing as subject, allow 'manage'
        action = 'manage';
        subjectParts = parts;
    }

    const subject = subjectParts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).replace(/_/g, '')).join('');

    return [action, subject];
};

/**
 * Build CASL subject from backend permission string (for use in can() checks).
 * Example: permissionToSubject('sales.leads.view') -> 'SalesLeads'
 */
export const permissionToSubject = (permissionString) => {
    const parsed = parsePermission(permissionString);
    return parsed ? parsed[1] : null;
};

/**
 * Build CASL action from backend permission string.
 * Example: permissionToAction('sales.leads.view') -> 'read'
 */
export const permissionToAction = (permissionString) => {
    const parsed = parsePermission(permissionString);
    return parsed ? parsed[0] : null;
};

/**
 * Checks if a user has access to a specific department
 * Admin roles (from user.adminRoles or default) can access all departments
 */
export const canAccessDepartment = (userDepartment, targetDepartment, userRole, adminRoles = DEFAULT_ADMIN_ROLES) => {
    if (!userDepartment || !targetDepartment) {
        return false;
    }

    // Admin roles can access all departments
    if (adminRoles.includes(userRole)) {
        return true;
    }

    // Users can only access their own department
    return userDepartment === targetDepartment;
};

/**
 * Normalize permissions to string[] (backend may send { key, description } or string).
 */
const toPermissionKeys = (permissions) => {
    if (!Array.isArray(permissions)) return [];
    return permissions
        .map((p) => (typeof p === 'object' && p != null && typeof p.key === 'string' ? p.key : typeof p === 'string' ? p : null))
        .filter(Boolean);
};

/**
 * Extracts department information from user object
 * The user object may contain:
 * - user.department: user's department
 * - user.role / user.roles[0]: user's role
 * - user.permissions: array of permission strings or { key, description }
 * - user.adminRoles: array of roles that are admin-level (optional)
 * - user.resourceDepartments: mapping of resources to departments (optional)
 */
export const getUserMetadata = (user) => {
    if (!user) {
        return {
            department: null,
            role: 'guest',
            permissions: [],
            adminRoles: DEFAULT_ADMIN_ROLES,
            resourceDepartments: {},
        };
    }

    const role = user.role ?? (Array.isArray(user.roles) && user.roles.length ? user.roles[0] : null) ?? 'guest';
    const permissions = toPermissionKeys(user.permissions);

    return {
        department: user.department || null,
        role: role === null || role === undefined ? 'guest' : role,
        permissions,
        adminRoles: Array.isArray(user.adminRoles) ? user.adminRoles : DEFAULT_ADMIN_ROLES,
        resourceDepartments: user.resourceDepartments || {},
    };
};

/**
 * Extracts roles array from user (for auth store / refresh).
 * Uses user.roles if array, otherwise [user.role] or [].
 */
export const extractRolesFromUser = (user) => {
    if (!user) return { roles: [] };
    const roles = Array.isArray(user.roles) ? user.roles : user.role != null ? [user.role] : [];
    return { roles };
};

/**
 * Gets the department for a resource/subject
 * First checks user.resourceDepartments mapping, then falls back to inference
 */
export const getDepartmentFromSubject = (subject, resourceDepartments = {}) => {
    // Check explicit mapping from user object
    if (resourceDepartments[subject]) {
        return resourceDepartments[subject];
    }

    // If no explicit mapping, return null (resource is department-agnostic)
    // The backend should provide resourceDepartments mapping if department isolation is needed
    return null;
};

// Centralized Project-related RBAC permission constants
export const PROJECT_MILESTONES_CREATE = 'projects.milestones.create';
export const PROJECT_MILESTONES_UPDATE = 'projects.milestones.update';
export const PROJECT_MILESTONES_DELETE = 'projects.milestones.delete';

export const PROJECT_PHASES_CREATE = 'projects.phase.create';
export const PROJECT_PHASES_UPDATE = 'projects.phase.update';
export const PROJECT_PHASES_DELETE = 'projects.phase.delete';

export const PROJECT_SPRINTS_VIEW_ABILITY = 'projects.sprints.view';
export const PROJECT_SPRINTS_CREATE_ABILITY = 'projects.sprints.create';
export const PROJECT_SPRINTS_UPDATE_ABILITY = 'projects.sprints.update';
export const PROJECT_SPRINTS_DELETE_ABILITY = 'projects.sprints.delete';

export const PROJECT_TASKS_VIEW_ABILITY = 'projects.tasks.view';
export const PROJECT_TASKS_CREATE_ABILITY = 'projects.tasks.create';
export const PROJECT_TASKS_UPDATE_ABILITY = 'projects.tasks.update';
export const PROJECT_TASKS_DELETE_ABILITY = 'projects.tasks.delete';
export const PROJECT_TASKS_MARK_TODO_ABILITY = 'projects.tasks.mark_todo';
export const PROJECT_TASKS_MARK_COMPLETE_ABILITY = 'projects.tasks.mark_complete';
export const PROJECT_TASKS_ASSIGN_ABILITY = 'projects.tasks.assign';
export const PROJECT_TASKS_MY_TASKS_ABILITY = 'projects.tasks.my_tasks';

export const PROJECT_BLOCKERS_VIEW = 'projects.blockers.view';
export const PROJECT_BLOCKERS_CREATE = 'projects.blockers.create';
export const PROJECT_BLOCKERS_UPDATE = 'projects.blockers.update';

export const PROJECT_BUGS_VIEW = 'projects.bugs.view';
export const PROJECT_DISCUSSION_VIEW = 'projects.discussion.view';

export const PROJECT_FEEDBACK_VIEW = 'projects.feedback.view';
export const PROJECT_FEEDBACK_CREATE = 'projects.feedback.create';

export const PROJECT_ISSUES_AND_UPGRADES_VIEW = 'projects.issues_and_upgrades.view';
export const PROJECT_ISSUES_AND_UPGRADES_CREATE = 'projects.issues_and_upgrades.create';
export const PROJECT_ISSUES_AND_UPGRADES_UPDATE = 'projects.issues_and_upgrades.update';
export const PROJECT_ISSUES_AND_UPGRADES_SUBMIT = 'projects.issues_and_upgrades.submit';
export const PROJECT_ISSUES_AND_UPGRADES_RESOLVE = 'projects.issues_and_upgrades.resolve';
export const PROJECT_ISSUES_AND_UPGRADES_DELETE = 'projects.issues_and_upgrades.delete';

export const PROJECT_QA_VIEW_ABILITY = 'projects.qa.view';
export const PROJECT_QA_CREATE_ABILITY = 'projects.qa.create';

export const PROJECT_PMO_VIEW_ABILITY = 'projects.pmo.view';
export const PROJECT_PMO_SUBMIT_TO_CLIENT_ABILITY = 'projects.pmo.submit';
export const PROJECT_MILESTONE_SUBMISSIONS_SUBMIT_ABILITY = 'projects.milestone_submissions.submit';
export const PROJECT_PMO_RESPOND_ABILITY = 'projects.completion_requests.respond';
export const PROJECT_PMO_CREATE_CHANGE_REQUEST_ABILITY = 'projects.change_requests.create';
export const PROJECT_PMO_VIEW_CHANGE_REQUEST_ABILITY = 'projects.change_requests.view';
export const PROJECT_CHANGE_REQUESTS_VIEW = 'projects.change_requests.view';
export const PROJECT_CHANGE_REQUESTS_CREATE = 'projects.change_requests.create';
export const PROJECT_CHANGE_REQUESTS_RESOLVE = 'projects.change_requests.resolve';

export const PROJECT_PAYMENTS_VIEW = 'projects.payment.view';
export const PROJECT_PAYMENTS_UPDATE = 'projects.payment.update';

export const PROJECT_CLIENT_TICKETS_VIEW = 'projects.client_tickets.view';
export const PROJECT_CLIENT_TICKETS_CREATE = 'projects.client_tickets.create';
export const PROJECT_CLIENT_TICKETS_UPDATE = 'projects.client_tickets.update';
export const PROJECT_CLIENT_TICKETS_SUBMIT = 'projects.client_tickets.submit';
export const PROJECT_CLIENT_TICKETS_SUBMIT_TO_CLIENT = 'projects.client_tickets.submit_to_client';
export const PROJECT_CLIENT_TICKETS_ACKNOWLEDGE = 'projects.client_tickets.acknowledge';

export const PROJECT_COLLABORATION_VIEW = 'projects.collaboration.view';
export const PROJECT_COLLABORATION_INVITE = 'projects.collaboration.invite';
export const PROJECT_COLLABORATION_ASSIGN = 'projects.collaboration.assign';

export const PROJECT_COMPLETION_REQUESTS_VIEW = 'projects.completion_requests.view';

export const PROJECT_SETTINGS_VIEW = 'projects.settings.view';
export const PROJECT_SETTINGS_UPDATE = 'projects.settings.update';
export const PROJECT_DELETE = 'projects.projects.delete';

export const PROJECT_TEMPLATES_MANAGE = 'projects.templates.manage';
export const PROJECT_SUBMISSION_MEMOS_CREATE = 'projects.submission_memos.create';
export const PROJECT_SUBMISSION_MEMOS_UPDATE = 'projects.submission_memos.update';
export const PROJECT_SUBMISSION_MEMOS_DELETE = 'projects.submission_memos.delete';
export const PROJECT_BUILDS_VIEW = 'projects.builds.view';
export const PROJECT_BUILDS_VIEW_ALL = 'projects.builds.view_all';
export const PROJECT_BUILDS_CREATE = 'projects.builds.create';
export const PROJECT_BUILDS_UPDATE = 'projects.builds.update';
export const PROJECT_BUILDS_DELETE = 'projects.builds.delete';
export const PROJECT_CLIENT_VIEW = 'projects.client.view';
export const PROJECT_DETAILS_UPDATE = 'projects.details.update';

export const PROJECT_TEAM_ACTIVITY_VIEW = 'projects.team_activity.view';
export const PROJECT_TEAM_WORKLOAD_VIEW = 'projects.team_workload.view';

